import { useState, useRef } from 'react';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  DataTable,
  PageHeader,
  MetricCard,
  LoadingState,
  Column,
} from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { BulkProcessor, BulkReport, parseWarpCSV } from '@/services/financePayroll';
import type { ExistingReport } from './FinanceXPayroll.container';

type Props = {
  processors: BulkProcessor[];
  reports: BulkReport[];
  isLoading: boolean;
  showUpload: boolean;
  onShowUpload: () => void;
  onCloseUpload: () => void;
  uploading: boolean;
  uploadError: string | null;
  onUpload: (
    vendorId: string,
    file: File,
    period: { start: string; end: string },
    force?: boolean
  ) => void;
  periodClash: ExistingReport[] | null;
  onDismissClash: () => void;
  onRowClick: (report: BulkReport) => void;
};

const fmt = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const fmtDate = (d: string): string => {
  if (!d) return '—';
  const date = new Date(`${d}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const columns: Column<BulkReport>[] = [
  {
    id: 'vendorName',
    label: 'Processor',
    render: (r) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionOutlinedIcon sx={{ fontSize: 18, color: '#71717A' }} />
        <Typography variant="body2" fontWeight={500}>{r.vendorName}</Typography>
      </Box>
    ),
  },
  {
    id: 'periodStart',
    label: 'Period',
    render: (r) => (
      <Typography variant="body2">
        {fmtDate(r.periodStart)} — {fmtDate(r.periodEnd)}
      </Typography>
    ),
  },
  {
    id: 'paidDate',
    label: 'Paid Date',
    render: (r) => <Typography variant="body2">{fmtDate(r.paidDate ?? '')}</Typography>,
  },
  {
    id: 'itemCount',
    label: 'People',
    align: 'center' as const,
    render: (r) => <Chip label={r.itemCount} size="small" />,
  },
  {
    id: 'totalGross',
    label: 'Gross Total',
    align: 'right' as const,
    render: (r) => <Typography variant="body2" fontWeight={500}>{fmt(r.totalGross)}</Typography>,
  },
  {
    id: 'totalNet',
    label: 'Net Pay',
    align: 'right' as const,
    render: (r) => <Typography variant="body2">{fmt(r.totalNet)}</Typography>,
  },
  {
    id: 'totalFees',
    label: 'Fees',
    align: 'right' as const,
    render: (r) => (
      <Typography variant="body2" color={r.totalFees > 0 ? '#dc2626' : 'inherit'}>
        {fmt(r.totalFees)}
      </Typography>
    ),
  },
];

// --- Upload Dialog ---

type UploadDialogProps = {
  open: boolean;
  onClose: () => void;
  processors: BulkProcessor[];
  uploading: boolean;
  uploadError: string | null;
  onUpload: (
    vendorId: string,
    file: File,
    period: { start: string; end: string },
    force?: boolean
  ) => void;
  periodClash: ExistingReport[] | null;
  onDismissClash: () => void;
  isMobile: boolean;
};

/** YYYY-MM -> the first and last day of that month. */
const monthToPeriod = (month: string): { start: string; end: string } => {
  const [y, m] = month.split('-').map(Number);
  return {
    start: `${month}-01`,
    end: new Date(y, m, 0).toLocaleDateString('en-CA'),
  };
};

const fmtMonthLabel = (month: string): string => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const UploadDialog = ({
  open,
  onClose,
  processors,
  uploading,
  uploadError,
  onUpload,
  periodClash,
  onDismissClash,
  isMobile,
}: UploadDialogProps): JSX.Element => {
  const [vendorId, setVendorId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [month, setMonth] = useState('');
  const [detectedMonth, setDetectedMonth] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Parse on selection so the period can be shown and corrected BEFORE upload.
   * Previously the period came from row 0 of the CSV with nobody asked, which
   * is how the same journal landed in April three times.
   */
  const handleFileSelect = async (picked: File | null): Promise<void> => {
    setFile(picked);
    onDismissClash();
    if (!picked) return;
    try {
      const rows = parseWarpCSV(await picked.text());
      setRowCount(rows.length);
      const csvMonth = rows[0]?.periodStart?.slice(0, 7) ?? '';
      setDetectedMonth(csvMonth || null);
      if (csvMonth) setMonth(csvMonth);
    } catch {
      setRowCount(0);
      setDetectedMonth(null);
    }
  };

  const handleSubmit = (force = false): void => {
    if (vendorId && file && month) {
      onUpload(vendorId, file, monthToPeriod(month), force);
    }
  };

  const handleClose = (): void => {
    setVendorId('');
    setFile(null);
    setMonth('');
    setDetectedMonth(null);
    setRowCount(0);
    onDismissClash();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={isMobile ? { sx: { borderRadius: '16px 16px 0 0', mt: 'auto', maxHeight: '90vh' } } : {}}
    >
      <DialogTitle>Upload Payroll Report</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {uploadError && <Alert severity="error">{uploadError}</Alert>}

        <TextField
          select
          label="Payment Processor"
          value={vendorId}
          onChange={(e) => setVendorId(e.target.value)}
          fullWidth
          size="small"
        >
          {processors.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.displayName}</MenuItem>
          ))}
        </TextField>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
        />

        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed #F4F4F5',
            borderRadius: '12px',
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: '200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
            '&:hover': { borderColor: '#054AD9', background: '#f8f9ff' },
            '&:active': { borderColor: '#054AD9', background: '#f0f3ff' },
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: '#71717A', mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {file ? file.name : 'Click to select CSV file'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supports Warp Payroll CSV format
          </Typography>
        </Box>

        {/* Period is explicit and editable — never silently taken from the file. */}
        {file && (
          <TextField
            type="month"
            label="Pay period (month)"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              onDismissClash();
            }}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText={
              detectedMonth
                ? `Detected ${fmtMonthLabel(detectedMonth)} from the file · ${rowCount} people`
                : `Not found in the file — please pick the month · ${rowCount} people`
            }
          />
        )}

        {periodClash && periodClash.length > 0 && (
          <Alert severity="warning">
            <strong>
              {month ? fmtMonthLabel(month) : 'This period'} already has{' '}
              {periodClash.length === 1 ? 'a report' : `${periodClash.length} reports`}.
            </strong>
            <Box component="ul" sx={{ m: '8px 0 0', pl: 2.5 }}>
              {periodClash.map((r) => (
                <li key={r.id}>
                  {r.vendorName} · {r.itemCount} people ·{' '}
                  {r.totalGross.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  {r.sourceFileName ? ` · ${r.sourceFileName}` : ''}
                </li>
              ))}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Uploading again counts every contractor twice in COGS. Delete the existing report
              first unless this is genuinely a separate pay run.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        {periodClash && periodClash.length > 0 && (
          <Button color="warning" onClick={() => handleSubmit(true)} disabled={uploading}>
            Upload anyway
          </Button>
        )}
        <Button
          variant="contained"
          onClick={() => handleSubmit(false)}
          disabled={!vendorId || !file || !month || uploading || !!periodClash}
          sx={{ borderRadius: '999px', textTransform: 'none' }}
        >
          {uploading ? 'Processing...' : 'Upload & Parse'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main Component ---

const FinanceXPayrollComponent = ({
  processors,
  reports,
  isLoading,
  showUpload,
  onShowUpload,
  onCloseUpload,
  uploading,
  uploadError,
  onUpload,
  periodClash,
  onDismissClash,
  onRowClick,
}: Props): JSX.Element => {
  const { isMobile } = useBreakpoint();

  const totalGross = reports.reduce((s, r) => s + r.totalGross, 0);
  const totalFees = reports.reduce((s, r) => s + r.totalFees, 0);
  const totalPeople = reports.reduce((s, r) => s + r.itemCount, 0);

  if (isLoading) return <LoadingState message="Loading payroll data..." />;

  return (
    <>
      <PageHeader
        title="Payroll Reports"
        actions={
          <Button
            variant="contained"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={onShowUpload}
            sx={{ borderRadius: '999px', textTransform: 'none' }}
          >
            Upload Report
          </Button>
        }
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <MetricCard label="Reports" value={reports.length} />
        <MetricCard label="Total Paid" value={fmt(totalGross)} />
        <MetricCard label="Total Fees" value={fmt(totalFees)} color="#dc2626" />
        <MetricCard label="People Paid" value={totalPeople} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {processors.map((p) => (
          <Chip
            key={p.id}
            label={`${p.displayName} (${p.reportCount})`}
            variant="outlined"
            size="small"
          />
        ))}
      </Box>

      <DataTable
        columns={columns}
        data={reports}
        isLoading={isLoading}
        rowKey={(r) => r.id}
        onRowClick={onRowClick}
        searchPlaceholder="Search reports..."
        searchFields={(r) => `${r.vendorName} ${r.sourceFileName ?? ''}`}
        emptyMessage="No payroll reports uploaded yet. Upload a CSV from Warp, Deel, or other payment processors."
      />

      <UploadDialog
        open={showUpload}
        onClose={onCloseUpload}
        processors={processors}
        uploading={uploading}
        uploadError={uploadError}
        onUpload={onUpload}
        periodClash={periodClash}
        onDismissClash={onDismissClash}
        isMobile={isMobile}
      />
    </>
  );
};

export default FinanceXPayrollComponent;
